import React, { useState, useEffect, useMemo } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '@/components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
    Phone,
    PhoneIncoming,
    PhoneOutgoing,
    PhoneMissed,
    ExternalLink,
    Clock,
    CheckCircle,
    AlertCircle,
    XCircle,
    RefreshCw,
    Loader2,
    ClipboardCheck,
    Target,
    Shield,
    Sparkles,
    AlertTriangle,
    FileText,
    ChevronDown,
    ChevronUp,
    Play,
} from 'lucide-react';
import logger from '@/lib/logger';

const CATEGORY_META = {
    critical: { label: 'Critical', color: 'text-red-500', bg: 'bg-red-500/10', border: 'border-red-500/30', icon: Shield, max: 6 },
    core: { label: 'Core Selling', color: 'text-blue-500', bg: 'bg-blue-500/10', border: 'border-blue-500/30', icon: Target, max: 8 },
    quality: { label: 'Quality', color: 'text-purple-500', bg: 'bg-purple-500/10', border: 'border-purple-500/30', icon: Sparkles, max: 6 },
};

const SCORE_LABELS = {
    0: { text: 'Not Met', color: 'bg-red-500 text-white', desc: 'Missing, incorrect, or unprofessional' },
    1: { text: 'Partial', color: 'bg-amber-500 text-white', desc: 'Present but incomplete or weak' },
    2: { text: 'Fully Met', color: 'bg-green-500 text-white', desc: 'Clear, confident & professional' },
};

const VERDICT_STYLES = {
    pass: { label: 'PASS', bg: 'bg-green-500', text: 'text-green-500', border: 'border-green-500', range: '16-20' },
    review: { label: 'REVIEW', bg: 'bg-amber-500', text: 'text-amber-500', border: 'border-amber-500', range: '13-15' },
    fail: { label: 'FAIL', bg: 'bg-red-500', text: 'text-red-500', border: 'border-red-500', range: '0-12' },
};

const QCDashboardPage = () => {
    const [dailyCalls, setDailyCalls] = useState([]);
    const [allCalls, setAllCalls] = useState([]);
    const [qcQuestions, setQcQuestions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('daily'); // daily | all | reviewed
    const [reviewCall, setReviewCall] = useState(null);
    const [threecxUrl, setThreecxUrl] = useState('');
    const [assignment, setAssignment] = useState(null);

    const fetchDailyAssignment = async () => {
        setLoading(true);
        try {
            const res = await api.get('/3cx/calls/qc-daily');
            setDailyCalls(res.data.calls || []);
            setQcQuestions(res.data.qc_questions || []);
            setAssignment(res.data.assignment || null);
        } catch (err) {
            logger.error(err);
        } finally {
            setLoading(false);
        }
    };

    const fetchAllCalls = async (status = '') => {
        setLoading(true);
        try {
            const params = {};
            if (status) params.status = status;
            const res = await api.get('/3cx/calls/qc-queue', { params });
            setAllCalls(res.data.calls || []);
            setThreecxUrl(res.data.threecx_recordings_url || '');
            if (!qcQuestions.length) setQcQuestions(res.data.qc_questions || []);
        } catch (err) {
            logger.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchDailyAssignment(); }, []);
    useEffect(() => {
        if (activeTab === 'all') fetchAllCalls();
        else if (activeTab === 'reviewed') fetchAllCalls('reviewed');
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab]);

    const activeCalls = activeTab === 'daily' ? dailyCalls : allCalls;
    const pendingCount = dailyCalls.filter(c => !c.qc_status || c.qc_status === 'pending').length;
    const reviewedCount = dailyCalls.filter(c => c.qc_status === 'reviewed').length;

    // Stats
    const stats = useMemo(() => {
        const reviewed = dailyCalls.filter(c => c.qc_verdict);
        return {
            total: dailyCalls.length,
            pending: pendingCount,
            reviewed: reviewedCount,
            passed: reviewed.filter(c => c.qc_verdict === 'pass').length,
            failed: reviewed.filter(c => c.qc_verdict === 'fail').length,
            avgScore: reviewed.length ? (reviewed.reduce((s, c) => s + (c.qc_total_score || 0), 0) / reviewed.length).toFixed(1) : '—',
        };
    }, [dailyCalls, pendingCount, reviewedCount]);

    const handleReviewDone = () => {
        setReviewCall(null);
        fetchDailyAssignment();
        if (activeTab !== 'daily') fetchAllCalls(activeTab === 'reviewed' ? 'reviewed' : '');
    };

    const formatDuration = (s) => { if (!s) return '0:00'; return `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`; };
    const formatDate = (d) => { if (!d) return ''; return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }); };

    return (
        <div className="space-y-4" data-testid="qc-dashboard">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold flex items-center gap-2">
                        <ClipboardCheck className="h-6 w-6 text-purple-500" /> QC Scoring Dashboard
                    </h1>
                    <p className="text-sm text-muted-foreground">
                        {assignment ? `Today's Assignment: ${assignment.total_calls} calls from ${(assignment.details || []).reduce((s, d) => { s.add(d.team); return s; }, new Set()).size} team(s)` : 'Review and score sales calls per SOP guidelines'}
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    {threecxUrl && (
                        <a href={threecxUrl} target="_blank" rel="noopener noreferrer">
                            <Button variant="outline" size="sm"><ExternalLink className="h-3.5 w-3.5 mr-1" /> 3CX Recordings</Button>
                        </a>
                    )}
                    <Button variant="outline" size="sm" onClick={fetchDailyAssignment}>
                        <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
                    </Button>
                </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-6 gap-3">
                {[
                    { label: "Today's Calls", value: stats.total, icon: Phone, color: 'text-blue-500', bg: 'bg-blue-500/10' },
                    { label: 'Pending', value: stats.pending, icon: Clock, color: 'text-amber-500', bg: 'bg-amber-500/10' },
                    { label: 'Reviewed', value: stats.reviewed, icon: CheckCircle, color: 'text-green-500', bg: 'bg-green-500/10' },
                    { label: 'Passed', value: stats.passed, icon: CheckCircle, color: 'text-emerald-500', bg: 'bg-emerald-500/10' },
                    { label: 'Failed', value: stats.failed, icon: XCircle, color: 'text-red-500', bg: 'bg-red-500/10' },
                    { label: 'Avg Score', value: `${stats.avgScore}/20`, icon: Target, color: 'text-purple-500', bg: 'bg-purple-500/10' },
                ].map(s => (
                    <Card key={s.label}>
                        <CardContent className="pt-4 pb-3">
                            <div className="flex items-center gap-2">
                                <div className={`p-1.5 rounded ${s.bg}`}><s.icon className={`h-4 w-4 ${s.color}`} /></div>
                                <div>
                                    <p className="text-[10px] text-muted-foreground">{s.label}</p>
                                    <p className="text-lg font-bold">{s.value}</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </div>

            {/* Tab Switcher */}
            <div className="flex gap-1 border-b">
                {[
                    { id: 'daily', label: `Today's Assignment (${dailyCalls.length})` },
                    { id: 'all', label: 'All Calls' },
                    { id: 'reviewed', label: 'Reviewed' },
                ].map(tab => (
                    <button
                        key={tab.id}
                        className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                            activeTab === tab.id ? 'border-purple-500 text-purple-500' : 'border-transparent text-muted-foreground hover:text-foreground'
                        }`}
                        onClick={() => setActiveTab(tab.id)}
                        data-testid={`tab-${tab.id}`}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* Call List */}
            {loading ? (
                <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-6 w-6 animate-spin text-purple-500" />
                </div>
            ) : activeCalls.length === 0 ? (
                <Card><CardContent className="py-12 text-center text-muted-foreground">No calls to display</CardContent></Card>
            ) : (
                <div className="space-y-2">
                    {activeCalls.map(call => (
                        <CallRow key={call.call_id} call={call} onReview={() => setReviewCall(call)} />
                    ))}
                </div>
            )}

            {/* Review Modal */}
            {reviewCall && (
                <ReviewModal
                    call={reviewCall}
                    questions={qcQuestions}
                    onClose={() => setReviewCall(null)}
                    onSaved={handleReviewDone}
                />
            )}
        </div>
    );
};

// ── Call Row ───────────────────────────────────
function CallRow({ call, onReview }) {
    const isPending = !call.qc_status || call.qc_status === 'pending';
    const verdict = call.qc_verdict;
    const vs = verdict ? VERDICT_STYLES[verdict] : null;

    return (
        <div
            className={`flex items-center justify-between p-3 rounded-lg border transition-colors cursor-pointer hover:bg-muted/50 ${
                isPending ? 'border-amber-500/30 bg-amber-500/5' : verdict === 'fail' ? 'border-red-500/20' : verdict === 'pass' ? 'border-green-500/20' : 'border-muted'
            }`}
            onClick={onReview}
            data-testid={`call-row-${call.call_id}`}
        >
            <div className="flex items-center gap-3">
                {call.call_direction === 'Inbound' ? <PhoneIncoming className="h-4 w-4 text-blue-500" />
                    : call.call_type === 'Missed' ? <PhoneMissed className="h-4 w-4 text-red-500" />
                    : <PhoneOutgoing className="h-4 w-4 text-green-500" />}
                <div>
                    <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">{call.contact_name || call.phone_number || 'Unknown'}</span>
                        <span className="text-xs text-muted-foreground">{call.phone_number}</span>
                    </div>
                    <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                        <span>Ext {call.agent_extension}</span>
                        <span>{new Date(call.call_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                        <span className="font-mono">{Math.floor((call.call_duration || 0) / 60)}:{((call.call_duration || 0) % 60).toString().padStart(2, '0')}</span>
                    </div>
                </div>
            </div>
            <div className="flex items-center gap-2">
                {call.recording_url && <Badge variant="outline" className="text-[9px]"><Play className="h-2.5 w-2.5 mr-0.5" />Recording</Badge>}
                {call.nlp_status === 'completed' && <Badge variant="outline" className="text-[9px] text-purple-500 border-purple-500/30"><FileText className="h-2.5 w-2.5 mr-0.5" />Transcript</Badge>}
                {vs ? (
                    <div className="flex items-center gap-1.5">
                        <span className="text-xs font-mono font-bold">{call.qc_total_score}/20</span>
                        <Badge className={`${vs.bg} text-white text-[10px] px-2`}>{vs.label}</Badge>
                    </div>
                ) : (
                    <Badge className="bg-amber-500/10 text-amber-600 border-amber-500/30 text-[10px]">Pending Review</Badge>
                )}
            </div>
        </div>
    );
}

// ── Review Modal ──────────────────────────────
function ReviewModal({ call, questions, onClose, onSaved }) {
    const [scores, setScores] = useState(() => {
        if (call.qc_scores) return { ...call.qc_scores };
        const init = {};
        questions.forEach(q => { init[q.id] = -1; }); // -1 = not scored yet
        return init;
    });
    const [comments, setComments] = useState(() => call.qc_comments || {});
    const [notes, setNotes] = useState(call.qc_notes || '');
    const [saving, setSaving] = useState(false);
    const [showTranscript, setShowTranscript] = useState(false);

    const allScored = questions.every(q => scores[q.id] !== undefined && scores[q.id] >= 0);

    // Live result calculation
    const liveResult = useMemo(() => {
        if (!allScored) return null;
        const total = questions.reduce((s, q) => s + (scores[q.id] || 0), 0);
        const criticalZero = ['q2', 'q6', 'q10'].some(qid => (scores[qid] || 0) === 0);
        let result;
        if (criticalZero) result = 'review';
        else if (total >= 16) result = 'pass';
        else if (total >= 13) result = 'review';
        else result = 'fail';

        return {
            total,
            result,
            criticalZero,
            critical: questions.filter(q => q.category === 'critical').reduce((s, q) => s + (scores[q.id] || 0), 0),
            core: questions.filter(q => q.category === 'core').reduce((s, q) => s + (scores[q.id] || 0), 0),
            quality: questions.filter(q => q.category === 'quality').reduce((s, q) => s + (scores[q.id] || 0), 0),
        };
    }, [scores, questions, allScored]);

    const handleSubmit = async () => {
        if (!allScored) { toast.error('Please score all 10 questions'); return; }
        setSaving(true);
        try {
            const cleanScores = {};
            questions.forEach(q => { cleanScores[q.id] = scores[q.id]; });
            await api.put(`/3cx/calls/${call.call_id}/qc`, {
                qc_scores: cleanScores,
                qc_comments: comments,
                qc_notes: notes,
                qc_status: 'reviewed',
            });
            toast.success(`Review saved — ${liveResult.result.toUpperCase()} (${liveResult.total}/20)`);
            onSaved();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to save review');
        } finally {
            setSaving(false);
        }
    };

    const vs = liveResult ? VERDICT_STYLES[liveResult.result] : null;

    return (
        <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto p-0" data-testid="review-modal">
                {/* Header */}
                <div className="sticky top-0 z-10 bg-background border-b p-4">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-lg">
                            <ClipboardCheck className="h-5 w-5 text-purple-500" />
                            QC Review — {call.contact_name || call.phone_number}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{call.phone_number}</span>
                        <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{Math.floor((call.call_duration || 0) / 60)}:{((call.call_duration || 0) % 60).toString().padStart(2, '0')}</span>
                        <span>Ext {call.agent_extension}</span>
                        <span>{new Date(call.call_date).toLocaleString()}</span>
                        {call.recording_url && (
                            <a href={call.recording_url} target="_blank" rel="noopener noreferrer" className="text-purple-500 hover:underline flex items-center gap-1">
                                <Play className="h-3 w-3" /> Play Recording
                            </a>
                        )}
                    </div>

                    {/* Live Score Bar */}
                    {liveResult && (
                        <div className={`mt-3 flex items-center justify-between p-3 rounded-lg border-2 ${vs.border} ${vs.bg}/10`} data-testid="live-score">
                            <div className="flex items-center gap-4">
                                <div className="text-center">
                                    <span className={`text-2xl font-bold ${vs.text}`}>{liveResult.total}</span>
                                    <span className="text-sm text-muted-foreground">/20</span>
                                </div>
                                <Badge className={`${vs.bg} text-white text-sm px-3 py-1`}>{vs.label}</Badge>
                                {liveResult.criticalZero && (
                                    <span className="text-[10px] text-amber-500 flex items-center gap-1">
                                        <AlertTriangle className="h-3 w-3" /> Critical Q scored 0 — Auto Review
                                    </span>
                                )}
                            </div>
                            <div className="flex gap-3 text-[10px]">
                                <span className="text-red-500">Critical: {liveResult.critical}/6</span>
                                <span className="text-blue-500">Core: {liveResult.core}/8</span>
                                <span className="text-purple-500">Quality: {liveResult.quality}/6</span>
                            </div>
                        </div>
                    )}
                </div>

                {/* Transcript preview */}
                {call.transcript && (
                    <div className="mx-4 mt-3">
                        <button
                            className="flex items-center gap-1 text-xs text-purple-500 hover:underline mb-1"
                            onClick={() => setShowTranscript(!showTranscript)}
                        >
                            <FileText className="h-3 w-3" />
                            {showTranscript ? 'Hide' : 'Show'} AI Transcript
                            {showTranscript ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                        </button>
                        {showTranscript && (
                            <div className="p-3 border rounded-lg bg-muted/30 max-h-40 overflow-y-auto text-[11px] text-muted-foreground whitespace-pre-wrap">
                                {call.transcript}
                            </div>
                        )}
                    </div>
                )}

                {/* Scoring Form */}
                <div className="p-4 space-y-3">
                    {questions.map((q, idx) => {
                        const cat = CATEGORY_META[q.category];
                        const CatIcon = cat.icon;
                        const isScored = scores[q.id] >= 0;
                        const isCritical = ['q2', 'q6', 'q10'].includes(q.id);

                        return (
                            <div
                                key={q.id}
                                className={`p-3 rounded-lg border transition-colors ${
                                    isScored ? (scores[q.id] === 0 ? 'border-red-500/30 bg-red-500/5' : scores[q.id] === 1 ? 'border-amber-500/30 bg-amber-500/5' : 'border-green-500/30 bg-green-500/5')
                                    : 'border-muted'
                                }`}
                                data-testid={`question-${q.id}`}
                            >
                                <div className="flex items-start justify-between gap-3">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2 mb-1">
                                            <span className="text-xs font-bold text-muted-foreground">Q{idx + 1}</span>
                                            <span className="text-sm font-medium">{q.label}</span>
                                            <Badge className={`${cat.bg} ${cat.color} ${cat.border} text-[9px] px-1.5 py-0`}>
                                                <CatIcon className="h-2.5 w-2.5 mr-0.5" />{cat.label}
                                            </Badge>
                                            {isCritical && <Badge variant="outline" className="text-[9px] text-red-500 border-red-500/30 px-1 py-0">If 0 → Auto Review</Badge>}
                                        </div>
                                        <p className="text-[11px] text-muted-foreground">{q.criteria}</p>
                                    </div>

                                    {/* Score Buttons */}
                                    <div className="flex gap-1 shrink-0">
                                        {[0, 1, 2].map(score => (
                                            <button
                                                key={score}
                                                className={`w-16 h-8 rounded text-[10px] font-semibold border transition-all ${
                                                    scores[q.id] === score
                                                        ? `${SCORE_LABELS[score].color} border-transparent shadow-sm scale-105`
                                                        : 'bg-muted/50 text-muted-foreground border-muted hover:border-foreground/30'
                                                }`}
                                                onClick={() => setScores(prev => ({ ...prev, [q.id]: score }))}
                                                data-testid={`score-${q.id}-${score}`}
                                            >
                                                {SCORE_LABELS[score].text}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Per-question comment */}
                                {isScored && scores[q.id] < 2 && (
                                    <div className="mt-2">
                                        <Textarea
                                            value={comments[q.id] || ''}
                                            onChange={e => setComments(prev => ({ ...prev, [q.id]: e.target.value }))}
                                            placeholder={`Comment for ${q.label} (recommended for scores 0-1)...`}
                                            className="text-xs h-16 resize-none"
                                        />
                                    </div>
                                )}
                            </div>
                        );
                    })}

                    {/* Overall Notes */}
                    <div className="pt-2">
                        <label className="text-sm font-medium mb-1 block">Overall Notes</label>
                        <Textarea
                            value={notes}
                            onChange={e => setNotes(e.target.value)}
                            placeholder="Any additional comments about this call..."
                            className="text-xs h-20"
                        />
                    </div>
                </div>

                {/* Footer */}
                <DialogFooter className="sticky bottom-0 bg-background border-t p-4">
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button
                        onClick={handleSubmit}
                        disabled={!allScored || saving}
                        className="min-w-[140px]"
                        data-testid="submit-review-btn"
                    >
                        {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-1" />}
                        Submit Review {liveResult ? `(${liveResult.total}/20)` : ''}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default QCDashboardPage;
